import type { ReactNode } from 'react'
import { createContext, isValidElement, use } from 'react'

export type CompositionContextValue<
  State extends object,
  Actions extends object = Record<string, unknown>,
  Meta = never,
> = {
  actions: Actions
  state: State
} & ([Meta] extends [never] ? unknown : { meta: Meta })

interface NonNullableContextOptions<Value> {
  /**
   * @default null
   */
  initialValue?: Value | null
}

export function createNonNullableContext<Value>(
  name: string,
  { initialValue = null }: NonNullableContextOptions<Value> = {},
) {
  const Context = createContext<Value | null>(initialValue)
  const useSafeContext = () => {
    return use(Context)
  }

  return {
    Context,
    useRequiredContext: () => {
      const value = useSafeContext()
      if (value === null) {
        throw new Error(`must be used within a ${name}!`)
      }

      return value
    },
    useSafeContext,
  }
}

export function findLeafPrimitiveValue(node: ReactNode) {
  if (
    typeof node === 'string' ||
    typeof node === 'number' ||
    node === null ||
    node === undefined
  ) {
    return node
  }

  if (
    isValidElement<{
      children: ReactNode
      renderValue?: () => ReactNode
    }>(node)
  ) {
    return findLeafPrimitiveValue(
      node.props.renderValue?.() ?? node.props.children,
    )
  }

  return null
}
